import React, {useState, useRef, useEffect, useContext} from 'react';
import {StatusCode} from 'grpc-web';
import PulseLoader from "react-spinners/PulseLoader";

import {SummaryStatisticsRequest} from "../goveeenergymonitoringpb/internal/proto/goveeenergymonitoring_pb"
import {
  Bar,
  BarChart, CartesianGrid,
  ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import {AuthContext} from "../contexts/AuthContext";
import moment from "moment";

const tickLookup = [3600*1000*24*182, 3600*1000*24*63, 3600*1000*24*30, 3600*1000*24*14, 3600*1000*24*7, 3600*1000*24*2, 3600*1000*24, 3600*1000*12, 3600*1000*8, 3600*1000*6, 3600*1000*4, 3600*1000*3, 3600*1000*2, 60*60*1000, 45*60*1000, 30*60*1000, 20*60*1000, 10*60*1000, 5*60*1000, 2*60*1000, 60*1000, 40*1000, 30*1000, 20*1000, 10*1000, 5*1000, 4*1000, 2*1000, 1000, 500, 200, 100, 50];

const SmartPlugExtendedSummaryStatistics = ({client, device, title, queryIntervalMillis, bucketIntervalMillis, containerHeight}) => {
  const [summaryStats, setSummaryStats] = useState({});
  const [isLoadingChart, setIsLoadingChart] = useState(true);
  const { getAccessToken } = useContext(AuthContext);
  // console.log("Creating SmartPlug, device", device.toObject(), zoomIntervalMillis, new Date(since),"<->", new Date(until));
  containerHeight = containerHeight || "100%"
  const until = Date.now();
  const since = until - queryIntervalMillis;
  const zoomIntervalMillis = until - since;

  const requestIdRef = useRef(0);
  const timeoutRef = useRef(null);

  const [linearDomainMin, linearDomainMax] = [since, until];
  const durationMillis = linearDomainMax - linearDomainMin;
  const tickInterval = durationMillis / 3;
  // console.log("tick Interval", tickInterval);

  var modInterval = (zoomIntervalMillis/(24*3600*1000)) > 370
      ? 180*24*3600*1000
      : (durationMillis/(24*3600*1000)) > 190
          ? 60*24*3600*1000
          : (durationMillis/(24*3600*1000)) > 65
              ? 30*24*3600*1000
              : (durationMillis/(24*3600*1000)) > 15
                  ? 7*24*3600*1000
                  : (durationMillis/(24*3600*1000)) > 2
                      ? 24*3600*1000 : 3600*1000;//tickLookup.find((e) => tickInterval / 8 >= e);
  var selectedInterval = modInterval; //tickLookup.find((e) => tickInterval >= e);
  // console.log("selected interval", selectedInterval, "modInterval", modInterval);

  var start = linearDomainMin - (linearDomainMin % modInterval);
  var end = linearDomainMax;
  var tickValues = [];
  var i = start;
  for (i = start; i < end; i += selectedInterval) {
    tickValues.push(i);
  }


  useEffect(()=>{
    let cancelled = false;
    const requestId = ++requestIdRef.current;

    clearTimeout(timeoutRef.current);
    setIsLoadingChart(false);

    const accessToken = getAccessToken();
    const reqMetadata = {"Authorization": "Bearer " + accessToken}
    if (!accessToken) {
      return () => {
        cancelled = true;
      };
    }

    // Start delayed loading timer
    timeoutRef.current = setTimeout(() => {
      if (!cancelled && requestId === requestIdRef.current) {
        console.log("Loading timer for extended summary statistics request", requestId, "setting IsLoadingChart true");
        setIsLoadingChart(true);
      }
    }, 100);

    const summaryRequest = new SummaryStatisticsRequest(
        [device.getId(), since, until, bucketIntervalMillis])

    client.getSummaryStatistics(summaryRequest, reqMetadata, (err, response) => {
      if (cancelled || requestId !== requestIdRef.current) return;

      clearTimeout(timeoutRef.current);
      setIsLoadingChart(false);

      if (err) {
        if (err.code === StatusCode.UNAUTHENTICATED) {
          // Don't bother retrying and having burst of retry attempts.
          console.error(
              "Received auth error getting extended summary statistics measurements. Don't bother retrying, let device list refresh to the retry",
              err)
          return;
        }

        // No toast error message this level because it might be a burst
        console.error("Received error getting batch of extended summary statistics measurements", err);
      } else {
        const statistics = response.getStatistics();
        const samples = statistics.getSummarySamplesList()
        console.log("Received", samples.length, "extended summary statistics measurements for device", device.getId());

        setSummaryStats({
          seriesMetadata: statistics.getMetadataMap(),
          summarySamplesList: statistics.getSummarySamplesList(),
          lastVolts: statistics.getLastVolts(),
          lastAmps: statistics.getLastAmps(),
          trend: statistics.getLatestTrend(),
        });
      }
    });

    // Cleanup function to clear the timer if the component unmounts
    return () => {
      cancelled = true;
      clearTimeout(timeoutRef.current);
    };
  }, [device]);

  const formattedData = summaryStats.summarySamplesList && summaryStats.summarySamplesList.map((o) => ({
    "timestamp": o.getTime(),
    "totalwatts": o.getTotalWatts()/1000,
    "totalcents": o.getTotalCents(),
  })) || [];

  const tickXFormatter = (unixTime) => {
    if (zoomIntervalMillis >= 604800000) { // 1 week
      return moment(unixTime).utc().format('M/DD')
    } else if (zoomIntervalMillis >= 129600000) { // 36 hours
      return moment(unixTime).format('MM/DD h:mma');
    } else if (zoomIntervalMillis >= 360000) { // 6 minutes
      return moment(unixTime).format('h:mma');
    } else {
      return moment(unixTime).format('h:mm:ssa');
    }
  }

  var seriesName = "totalwatts";
  const tickYPowerFormatter = (value) => value.toFixed(1);

  const tickYHumidityFormatter = (value) => value.toFixed(2);

  const tickYDollarsFormatter = (value) => value.toFixed(2);

  const tickYDollarsCentsFormatter = (value) => value.toFixed(2);

  const tickYFormatter = (seriesName === "hourlydollars")
      ? tickYDollarsFormatter
      : tickYPowerFormatter

  const tooltipValuesFormatter = (value, name) => (name === "Time")
      ? moment(value).format('MMM DD, h:mm:ss a')
      : (Math.round(value * 100) / 100).toFixed(2);

  const tooltipFormatter = (seriesName === "hourlydollars")
      ? tickYDollarsCentsFormatter
      : tooltipValuesFormatter;


  const tooltipDateFormatter = (label, payload) => (label)
      ? (zoomIntervalMillis >= 39*604800000 ? moment(label).format('MMM DD, YYYY h:mm:ss a') : moment(label).format('MMM DD, h:mm:ss a'))
      : label;

  if (isLoadingChart) {
    return <>
        <ResponsiveContainer width='100%' height={containerHeight} style={{'display': 'flex', 'justifyContent': 'center', 'alignItems': 'center'}}>
          <PulseLoader speedMultiplier={0.8} color="#629af0"/>
        </ResponsiveContainer>
    </>
  }

  return (
      <>
        {formattedData.length > 0 &&
          <ResponsiveContainer width='100%' height={containerHeight}>
            <div className="energy-summary-title">
              {title}
            </div>
            <div style={{width: "100%", height: "100%"}}>
              <ResponsiveContainer width='100%' height="45%">
                <BarChart data={formattedData} barSize="2%"
                            responsive
                            margin={{
                              top: 10,
                              right: 10,
                              bottom: 10,
                              left: 0,
                            }}>
                    <CartesianGrid strokeDasharray="3 3"/>
                    <XAxis
                        dataKey='timestamp'
                        domain={([dataMin, dataMax]) => { const offset = zoomIntervalMillis / 50; return [dataMin - offset, dataMax + offset]; }}
                        ticks={tickValues}
                        tickFormatter={tickXFormatter}
                        name='Time'
                        type='number'
                    />
                    <YAxis yAxisId="0" orientation="left"
                           tickFormatter={tickYPowerFormatter}
                           name="kW" unit="kW"/>

                    <Bar
                        data={formattedData}
                        dataKey="totalwatts"
                        width="auto"
                        animationDuration={500}
                        fill="#ff3333"
                        stroke="#ff3333"
                        dot={false}
                        yAxisId="0"
                        name="Power"
                        type="stepAfter"
                        unit="kW"
                    />

                    <Tooltip
                        offset={30}
                        formatter={tooltipFormatter}
                        labelFormatter={tooltipDateFormatter}
                    />
                  </BarChart>
              </ResponsiveContainer>
              <ResponsiveContainer width='100%'  height="45%">
                <BarChart data={formattedData} barSize="2%"
                          responsive
                          margin={{
                            top: 10,
                            right: 10,
                            bottom: 10,
                            left: 0,
                          }}>
                  <CartesianGrid strokeDasharray="3 3"/>
                  <XAxis
                      dataKey='timestamp'
                      domain={([dataMin, dataMax]) => { const offset = zoomIntervalMillis / 50; return [dataMin - offset, dataMax + offset]; }}
                      ticks={tickValues}
                      tickFormatter={tickXFormatter}
                      name='Time'
                      type='number'
                  />
                  <YAxis yAxisId="0" orientation="left"
                         name="¢" unit="¢"/>

                  <Bar
                      data={formattedData}
                      dataKey="totalcents"
                      width="auto"
                      animationDuration={500}
                      fill="#629af0"
                      stroke="#629af0"
                      dot={false}
                      yAxisId="0"
                      name="Cost"
                      type="stepAfter"
                      unit="¢"
                  />

                  <Tooltip
                      offset={30}
                      formatter={tooltipFormatter}
                      labelFormatter={tooltipDateFormatter}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ResponsiveContainer>
        }
      </>
  );
};

export default SmartPlugExtendedSummaryStatistics;

